import React from 'react';

import { SEARCH_OPERATORS, SORT_OPTIONS } from '../../constants';
import {
  SearchForm,
  type SearchFormData,
  type SearchTypeOption,
  type FilterChipConfig,
  type MoreSectionConfig,
} from '../shared';

export type CasesSearchType =
  | 'keyword'
  | 'courtId'
  | 'docketNumber'
  | 'caseName'
  | 'assignedToJudge';

export interface CasesSearchFormData
  extends Omit<SearchFormData, 'searchType'> {
  searchType: CasesSearchType;
}

export interface CasesSearchFormProps {
  onSubmit?: (data: CasesSearchFormData) => void;
  initialSearchType?: CasesSearchType;
  initialQuery?: string;
  initialExactMatch?: boolean;
  sortValue?: string;
  isLoading?: boolean;
  disabled?: boolean;
}

const CASES_SEARCH_TYPE_OPTIONS: SearchTypeOption[] = [
  { value: 'keyword', label: 'Keyword', placeholder: 'Search by Keyword' },
  {
    value: 'courtId',
    label: 'Court ID',
    placeholder: 'Enter court ID (e.g., court_id: 12345)',
    prefill: 'court_id:',
  },
  {
    value: 'docketNumber',
    label: 'Docket Number',
    placeholder: 'Enter docket number (e.g., docketNumber: 12345)',
    prefill: 'docketNumber:',
  },
  {
    value: 'caseName',
    label: 'Case Name',
    placeholder: 'Enter case name (e.g., caseName: Smith v. Jones)',
    prefill: 'caseName:',
  },
  {
    value: 'assignedToJudge',
    label: 'Assigned to Judge',
    placeholder: 'Enter judge name (e.g., assignedTo: Smith)',
    prefill: 'assignedTo:',
  },
];

const CASES_FILTER_CHIP_CONFIGS: FilterChipConfig[] = [
  {
    id: 'jurisdiction',
    label: 'Jurisdiction',
    type: 'jurisdiction',
  },
  {
    id: 'caseName',
    label: 'Name',
    type: 'text',
    fieldLabel: 'Case Name',
    placeholder: 'e.g., Smith v. Johnson',
  },
  {
    id: 'docketNumber',
    label: 'Docket',
    type: 'text',
    fieldLabel: 'Docket Number',
    placeholder: 'e.g., 21-1234',
  },
  {
    id: 'judge',
    label: 'Judge Name',
    type: 'text',
    fieldLabel: 'Judge (or Assigned To Judge)',
    placeholder: 'e.g., John Smith',
  },
];

const CASES_MORE_SECTION_CONFIG: MoreSectionConfig = {
  id: 'more',
  label: 'More',
  popoverTitle: 'Add More Details',
  fields: [
    {
      id: 'attorneyName',
      label: 'Attorney Name',
      type: 'text',
      placeholder: 'e.g., Jane Doe',
    },
    {
      id: 'partyName',
      label: 'Party Name',
      type: 'text',
      placeholder: 'e.g., John Smith',
    },
    {
      id: 'orderBy',
      label: 'Search Results Order',
      type: 'select',
      options: SORT_OPTIONS.map((option) => ({
        value: option.value,
        label: option.display,
      })),
      defaultValue: 'score_desc',
    },
  ],
};

export const CasesSearchForm: React.FC<CasesSearchFormProps> = ({
  onSubmit,
  initialSearchType = 'keyword',
  initialQuery = '',
  initialExactMatch = true,
  sortValue,
  isLoading = false,
  disabled = false,
}) => {
  const handleSubmit = (data: SearchFormData) => {
    if (onSubmit) {
      onSubmit({
        ...data,
        searchType: data.searchType as CasesSearchType,
      });
    }
  };

  const initialFilters = sortValue
    ? { moreFilters: { orderBy: sortValue } }
    : undefined;

  return (
    <SearchForm
      title="Search Cases &amp; Opinions Across 2000+ Courts"
      proTip="Use the search type chips below to refine your search by keyword, case name, docket number, party name, or assigned judge."
      searchTypeOptions={CASES_SEARCH_TYPE_OPTIONS}
      filterChipConfigs={CASES_FILTER_CHIP_CONFIGS}
      moreSectionConfig={CASES_MORE_SECTION_CONFIG}
      onSubmit={handleSubmit}
      initialSearchType={initialSearchType}
      initialQuery={initialQuery}
      initialExactMatch={initialExactMatch}
      initialFilters={initialFilters}
      isLoading={isLoading}
      disabled={disabled}
      showExactMatch={true}
      operatorsData={SEARCH_OPERATORS}
      testId="cases-search-form"
    />
  );
};

export default CasesSearchForm;
